import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { TFilters, TOrderBy } from 'redux/types';
import { TPgListRequest } from 'services/api/paymentGateway/types';
import { PAGE_SIZE } from 'services/constants/env';
import { ESortDirection, TTableStructure } from 'ui/EasTable/types';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';

type TPgListOptions = {
	/** Filters outside the table (a month picker, a fixed scope). */
	extraFilters?: TFilters;
	/** Applied after the sorting chosen in the table; also the default order. */
	defaultOrder?: TOrderBy;
};

/**
 * Builds the `POST …/list` body (03 §0) from the URL of an `EasTableWithFilter`: `page`, `search`,
 * `sortIndex`/`sortDirection` and the column filters, in the standard `FilterParams` format. Only
 * columns whose selector is a field of the gateway list may be sortable or filterable.
 */
export const buildPgListRequest = (
	tableStructure: TTableStructure<any>[],
	search: string,
	{ extraFilters = [], defaultOrder }: TPgListOptions = {},
): TPgListRequest => {
	const params = queryString.parse(search);
	const filters = [
		...(getFiltersFromNavigateParams(tableStructure, params) as TFilters),
		...extraFilters,
	];
	const orderBy: TOrderBy[] = [];
	const sortColumn =
		params.sortIndex !== undefined && params.sortIndex !== null
			? tableStructure[+params.sortIndex]
			: undefined;
	if (sortColumn && sortColumn.selector !== 'actions') {
		orderBy.push({
			fieldName: String(sortColumn.selector),
			direction: String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
		});
	}
	if (defaultOrder && !orderBy.some((item) => item.fieldName === defaultOrder.fieldName)) {
		orderBy.push(defaultOrder);
	}
	return {
		pageNumber: params.page ? +params.page : 1,
		pageSize: PAGE_SIZE,
		searchString: String(params.search ?? ''),
		filters,
		orderBy,
	};
};

/**
 * Calls `load` with a fresh list request whenever the URL query (page, sort, filters, search) or
 * `extraFilters` change. `tableStructure` must be the full, stable structure of the table (sort
 * indexes refer to it), not the one filtered by the visible columns; `load` and `extraFilters`
 * must be stable (`useCallback` / `useMemo`).
 */
export const usePgListLoader = (
	tableStructure: TTableStructure<any>[],
	load: (request: TPgListRequest) => void,
	options: TPgListOptions = {},
) => {
	const { search } = useLocation();
	const { extraFilters, defaultOrder } = options;
	const orderKey = defaultOrder ? `${defaultOrder.fieldName}:${defaultOrder.direction}` : '';

	useEffect(() => {
		load(buildPgListRequest(tableStructure, search, { extraFilters, defaultOrder }));
		// `defaultOrder` is a literal at the call site: `orderKey` stands for it.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tableStructure, search, load, extraFilters, orderKey]);
};
